import type { ComboboxOption } from "@replo/design-system/components/combobox/ComboboxContext";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { ALargeSmall, ArrowLeft, ChevronUp, Pencil } from "lucide-react";
import { convertFontWeightToReadableString } from "replo-runtime/shared/utils/font";

const WeightCombobox: React.FC<{
  fontFamily: string;
  value: string;
  onChange: (value: string) => void;
  projectFonts: any[];
}> = ({ fontFamily, value, onChange, projectFonts }) => {
  const fontsForFamily = projectFonts.filter(
    (font) => font.name === fontFamily,
  );

  const weightOptions = fontsForFamily.map((font) => {
    const isItalic = font.style === "italic";
    const weightLabel = convertFontWeightToReadableString(
      font.weight.toString(),
    );
    const label = isItalic ? `${weightLabel} Italic` : weightLabel;
    const value = isItalic ? `${font.weight}-italic` : font.weight.toString();

    return { value, label };
  });

  return (
    <Combobox.Root options={weightOptions} value={value} onChange={onChange}>
      <Combobox.Trigger>
        <Combobox.SelectionButton
          title={
            weightOptions.find((opt) => opt.value === value)?.label || value
          }
          size="sm"
          layoutClassName="w-full"
        />
      </Combobox.Trigger>
      <Combobox.Popover>
        <Combobox.Content size="sm" UNSAFE_listClassName="min-w-52" />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

interface DesignLibraryTypographyProps {
  styleLibrary: {
    typography?: {
      h1?: { name: string; url: string; size: number; weight: number };
      h2?: { name: string; url: string; size: number; weight: number };
      h3?: { name: string; url: string; size: number; weight: number };
      h4?: { name: string; url: string; size: number; weight: number };
      body?: { name: string; url: string; size: number; weight: number };
    };
    colors?: any;
  } | null;
  onBack?: () => void;
  onCustomFontsClick?: () => void;
}

export const DesignLibraryTypography: React.FC<
  DesignLibraryTypographyProps
> = ({ styleLibrary, onBack, onCustomFontsClick }) => {
  const projectId = useCurrentProjectId();
  const utils = trpcUtils;

  const { mutateAsync: updateStyleLibrary } =
    trpc.styles.createOrUpdateStyleLibrary.useMutation({
      onSuccess: () => {
        void utils.styles.getStyleLibrary.invalidate();
      },
    });

  const { data: projectFonts = [] } = trpc.font.getFonts.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const [expandedFont, setExpandedFont] = React.useState<string | null>(null);

  // NOTE: (Berkley 07/08/2025) This will be removed when we work do onboarding, since their style library will be assigned defaults
  const defaultTypography = {
    h1: { name: "Aeonik", url: "", size: 48, weight: 700 },
    h2: { name: "Aeonik", url: "", size: 32, weight: 500 },
    h3: { name: "Aeonik", url: "", size: 28, weight: 500 },
    h4: { name: "Aeonik", url: "", size: 24, weight: 500 },
    body: { name: "Inter", url: "", size: 16, weight: 400 },
  };

  const typography = {
    h1: styleLibrary?.typography?.h1 ?? defaultTypography.h1,
    h2: styleLibrary?.typography?.h2 ?? defaultTypography.h2,
    h3: styleLibrary?.typography?.h3 ?? defaultTypography.h3,
    h4: styleLibrary?.typography?.h4 ?? defaultTypography.h4,
    body: styleLibrary?.typography?.body ?? defaultTypography.body,
  };

  const uniqueFonts = Array.from(
    new Set(projectFonts.map((font) => font.name)),
  );

  const fontOptions: ComboboxOption[] = uniqueFonts.map((fontName) => ({
    value: fontName,
    label: fontName,
  }));

  const sizeOptions: ComboboxOption[] = [
    { value: "12", label: "12px" },
    { value: "14", label: "14px" },
    { value: "16", label: "16px" },
    { value: "18", label: "18px" },
    { value: "20", label: "20px" },
    { value: "24", label: "24px" },
    { value: "28", label: "28px" },
    { value: "32", label: "32px" },
    { value: "36", label: "36px" },
    { value: "48", label: "48px" },
    { value: "56", label: "56px" },
    { value: "64", label: "64px" },
  ];

  const spacingOptions: ComboboxOption[] = [
    { value: "auto", label: "auto" },
    { value: "0", label: "0" },
    { value: "0.5", label: "0.5" },
    { value: "1", label: "1" },
    { value: "1.5", label: "1.5" },
    { value: "2", label: "2" },
  ];

  const headingStyles = [
    {
      name: "Heading 1",
      key: "h1",
      settings: typography.h1,
      previewText: "Aa",
    },
    {
      name: "Heading 2",
      key: "h2",
      settings: typography.h2,
      previewText: "Aa",
    },
    {
      name: "Heading 3",
      key: "h3",
      settings: typography.h3,
      previewText: "Aa",
    },
    {
      name: "Heading 4",
      key: "h4",
      settings: typography.h4,
      previewText: "Aa",
    },
  ];

  const bodyStyles = [
    {
      name: "Paragraph",
      key: "body",
      settings: typography.body,
      previewText: "Aa",
    },
  ];

  const handleChangeExpandedFont = (fontKey: string) => {
    const newExpandedFont = expandedFont === fontKey ? null : fontKey;
    setExpandedFont(newExpandedFont);
  };

  const handleTypographyChange = async (
    fontKey: string,
    property: "name" | "size" | "weight",
    value: string,
  ) => {
    if (!projectId || !styleLibrary?.colors) {
      return;
    }

    const currentFont = typography[fontKey as keyof typeof typography];
    const updatedFont = { ...currentFont };

    if (property === "name") {
      const selectedFont = projectFonts.find((font) => font.name === value);
      updatedFont.name = value;
      updatedFont.url = selectedFont?.url || "";
    } else if (property === "size") {
      updatedFont.size = Number.parseInt(value, 10);
    } else if (property === "weight") {
      const isItalic = value.includes("-italic");
      const newWeight = Number.parseInt(value.replace("-italic", ""), 10);
      const style = isItalic ? "italic" : "normal";

      const selectedFont = projectFonts.find(
        (font) =>
          font.name === currentFont.name &&
          Number.parseInt(font.weight, 10) === newWeight &&
          font.style === style,
      );
      updatedFont.weight = newWeight;
      updatedFont.url = selectedFont?.url || currentFont.url;
    }

    const updatedStyleLibrary = {
      colors: styleLibrary.colors,
      typography: {
        ...typography,
        [fontKey]: updatedFont,
      },
    };

    await updateStyleLibrary({
      projectId,
      styleLibrary: updatedStyleLibrary,
    });
  };

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-lg border border-border bg-white shadow-sm">
      <div className="flex flex-col gap-4 self-stretch p-6 border-b-0.5 border-border">
        <div className="flex justify-between items-center self-stretch">
          <div className="flex items-center gap-3">
            <IconButton
              variant="secondary"
              size="sm"
              layoutClassName="h-6 w-6"
              icon={<ArrowLeft size={16} className="text-default" />}
              onClick={() => {
                if (projectId) {
                  void utils.webContainer.getFileTree.invalidate({ projectId });
                }
                onBack?.();
              }}
            />
            <div className="text-lg font-semibold">Typography</div>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" size="sm" onClick={onCustomFontsClick}>
              Custom Fonts
            </Button>
            <Button variant="secondary" size="sm">
              Presets
            </Button>
          </div>
        </div>
        <div className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg bg-sheet">
          <div
            className="flex h-8 flex-col justify-center items-center self-stretch text-center"
            style={{
              fontFamily: typography.h1?.name
                ? `'${typography.h1.name}', sans-serif`
                : undefined,
              fontSize: "42px",
              fontWeight: typography.h1?.weight ?? 600,
            }}
          >
            Heading
          </div>
          <div
            style={{
              fontFamily: typography.body?.name
                ? `'${typography.body.name}', sans-serif`
                : undefined,
              fontSize: "16px",
              fontWeight: typography.body?.weight ?? 400,
            }}
          >
            This is your paragraph.
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col self-stretch overflow-y-auto no-scrollbar">
        <div className="flex flex-col gap-2 px-6 py-4 border-b-0.5 border-border">
          <div className="text-sm font-medium text-muted">Headings</div>
          <div className="flex flex-col gap-4">
            {headingStyles.map((heading) => (
              <div
                key={heading.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedFont === heading.key
                    ? "rounded-lg border border-border bg-white shadow-sm p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  if (expandedFont !== heading.key) {
                    handleChangeExpandedFont(heading.key);
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-23 rounded-lg bg-sheet flex items-center justify-center flex-shrink-0 transition-all duration-300 ease-in-out",
                      expandedFont === heading.key ? "h-35" : "h-16",
                    )}
                  >
                    <span
                      className="text-2xl font-medium"
                      style={{
                        fontFamily: heading.settings.name,
                      }}
                    >
                      {heading.previewText}
                    </span>
                  </div>
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col justify-center",
                        expandedFont === heading.key
                          ? "border-b-0.5 border-border pb-2"
                          : "min-h-16",
                      )}
                    >
                      <div className="text-base font-medium">
                        {heading.name}
                      </div>
                      {expandedFont !== heading.key && (
                        <div className="text-sm text-muted">
                          {heading.settings.name} • {heading.settings.size}px •{" "}
                          {heading.settings.weight}
                        </div>
                      )}
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedFont === heading.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div className="flex flex-col gap-4 min-h-0 w-full overflow-hidden">
                        <div className="flex flex-col gap-3 w-full">
                          <div className="flex gap-3 w-full">
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Font</span>
                              <Combobox.Root
                                options={fontOptions}
                                value={heading.settings.name}
                                onChange={(value: string) =>
                                  void handleTypographyChange(
                                    heading.key,
                                    "name",
                                    value,
                                  )
                                }
                              >
                                <Combobox.Trigger>
                                  <Combobox.SelectionButton
                                    title={heading.settings.name}
                                    size="sm"
                                    layoutClassName="w-full"
                                  />
                                </Combobox.Trigger>
                                <Combobox.Popover>
                                  <Combobox.Content
                                    size="sm"
                                    UNSAFE_listClassName="min-w-48"
                                  />
                                </Combobox.Popover>
                              </Combobox.Root>
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Size</span>
                              <Combobox
                                options={sizeOptions}
                                value={heading.settings.size.toString()}
                                onChange={(value: string) =>
                                  void handleTypographyChange(
                                    heading.key,
                                    "size",
                                    value,
                                  )
                                }
                                trigger={
                                  <Combobox.SelectionButton
                                    title={`${heading.settings.size}px`}
                                    size="sm"
                                    layoutClassName="w-full"
                                    startEnhancer={
                                      <ALargeSmall
                                        size={14}
                                        className="text-muted"
                                      />
                                    }
                                  />
                                }
                                size="sm"
                              />
                            </div>
                          </div>

                          <div className="flex gap-3 w-full">
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Weight</span>
                              <WeightCombobox
                                fontFamily={heading.settings.name}
                                value={(() => {
                                  const currentFont = projectFonts.find(
                                    (font) =>
                                      font.name === heading.settings.name &&
                                      font.url === heading.settings.url,
                                  );
                                  const isItalic =
                                    currentFont?.style === "italic";
                                  return isItalic
                                    ? `${heading.settings.weight}-italic`
                                    : heading.settings.weight.toString();
                                })()}
                                onChange={(value: string) =>
                                  void handleTypographyChange(
                                    heading.key,
                                    "weight",
                                    value,
                                  )
                                }
                                projectFonts={projectFonts}
                              />
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Line</span>
                              <Input
                                value="70px"
                                placeholder="70px"
                                size="sm"
                                startEnhancer={
                                  <ALargeSmall
                                    size={14}
                                    className="text-muted"
                                  />
                                }
                                layoutClassName="w-full"
                              />
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">
                                Spacing
                              </span>
                              <Combobox
                                options={spacingOptions}
                                value="auto"
                                onChange={() => {}}
                                trigger={
                                  <Combobox.SelectionButton
                                    title="auto"
                                    size="sm"
                                    layoutClassName="w-full"
                                    startEnhancer={
                                      <ALargeSmall
                                        size={14}
                                        className="text-muted"
                                      />
                                    }
                                  />
                                }
                                size="sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <IconButton
                    variant="secondary"
                    size="sm"
                    layoutClassName={twMerge(
                      "h-8 w-8",
                      expandedFont === heading.key
                        ? "self-start"
                        : "self-center",
                    )}
                    UNSAFE_className="opacity-0 group-hover:opacity-100 transition-opacity"
                    icon={
                      expandedFont === heading.key ? (
                        <ChevronUp size={14} className="text-default" />
                      ) : (
                        <Pencil size={14} className="text-default" />
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChangeExpandedFont(heading.key);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Body Section */}
        <div className="flex flex-col gap-2 px-6 py-4">
          <div className="text-sm font-medium text-muted">Body</div>
          <div className="flex flex-col gap-4">
            {bodyStyles.map((body) => (
              <div
                key={body.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedFont === body.key
                    ? "rounded-lg border border-border bg-white shadow-sm p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  if (expandedFont !== body.key) {
                    handleChangeExpandedFont(body.key);
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-23 rounded-lg bg-sheet flex items-center justify-center flex-shrink-0 transition-all duration-300 ease-in-out",
                      expandedFont === body.key ? "h-35" : "h-16",
                    )}
                  >
                    <span
                      className="text-xl"
                      style={{
                        fontFamily: body.settings.name,
                      }}
                    >
                      {body.previewText}
                    </span>
                  </div>
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col justify-center",
                        expandedFont === body.key
                          ? "border-b-0.5 border-border pb-2"
                          : "min-h-16",
                      )}
                    >
                      <div className="text-base font-medium">{body.name}</div>
                      {expandedFont !== body.key && (
                        <div className="text-sm text-muted">
                          {body.settings.name} • {body.settings.size}px •{" "}
                          {body.settings.weight}
                        </div>
                      )}
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedFont === body.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div className="flex flex-col gap-4 min-h-0 w-full overflow-hidden">
                        {/* Font Controls */}
                        <div className="flex flex-col gap-3 w-full">
                          {/* Font and Size Row */}
                          <div className="flex gap-3 w-full">
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Font</span>
                              <Combobox.Root
                                options={fontOptions}
                                value={body.settings.name}
                                onChange={(value: string) =>
                                  void handleTypographyChange(
                                    body.key,
                                    "name",
                                    value,
                                  )
                                }
                              >
                                <Combobox.Trigger>
                                  <Combobox.SelectionButton
                                    title={body.settings.name}
                                    size="sm"
                                    layoutClassName="w-full"
                                  />
                                </Combobox.Trigger>
                                <Combobox.Popover>
                                  <Combobox.Content
                                    size="sm"
                                    UNSAFE_listClassName="min-w-48"
                                  />
                                </Combobox.Popover>
                              </Combobox.Root>
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Size</span>
                              <Combobox
                                options={sizeOptions}
                                value={body.settings.size.toString()}
                                onChange={(value: string) =>
                                  void handleTypographyChange(
                                    body.key,
                                    "size",
                                    value,
                                  )
                                }
                                trigger={
                                  <Combobox.SelectionButton
                                    title={`${body.settings.size}px`}
                                    size="sm"
                                    layoutClassName="w-full"
                                    startEnhancer={
                                      <ALargeSmall
                                        size={14}
                                        className="text-muted"
                                      />
                                    }
                                  />
                                }
                                size="sm"
                              />
                            </div>
                          </div>

                          {/* Weight, Line, and Spacing Row */}
                          <div className="flex gap-3 w-full">
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Weight</span>
                              <WeightCombobox
                                fontFamily={body.settings.name}
                                value={(() => {
                                  const currentFont = projectFonts.find(
                                    (font) =>
                                      font.name === body.settings.name &&
                                      font.url === body.settings.url,
                                  );
                                  const isItalic =
                                    currentFont?.style === "italic";
                                  return isItalic
                                    ? `${body.settings.weight}-italic`
                                    : body.settings.weight.toString();
                                })()}
                                onChange={(value: string) =>
                                  void handleTypographyChange(
                                    body.key,
                                    "weight",
                                    value,
                                  )
                                }
                                projectFonts={projectFonts}
                              />
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">Line</span>
                              <Input
                                value="70px"
                                placeholder="70px"
                                size="sm"
                                startEnhancer={
                                  <ALargeSmall
                                    size={14}
                                    className="text-muted"
                                  />
                                }
                                layoutClassName="w-full"
                              />
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
                              <span className="text-sm text-muted">
                                Spacing
                              </span>
                              <Combobox
                                options={spacingOptions}
                                value="auto"
                                onChange={() => {
                                  // Spacing not implemented yet
                                }}
                                trigger={
                                  <Combobox.SelectionButton
                                    title="auto"
                                    size="sm"
                                    layoutClassName="w-full"
                                    startEnhancer={
                                      <ALargeSmall
                                        size={14}
                                        className="text-muted"
                                      />
                                    }
                                  />
                                }
                                size="sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <IconButton
                    variant="secondary"
                    size="sm"
                    layoutClassName={twMerge(
                      "h-8 w-8",
                      expandedFont === body.key ? "self-start" : "self-center",
                    )}
                    UNSAFE_className="opacity-0 group-hover:opacity-100 transition-opacity"
                    icon={
                      expandedFont === body.key ? (
                        <ChevronUp size={14} className="text-default" />
                      ) : (
                        <Pencil size={14} className="text-default" />
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChangeExpandedFont(body.key);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
